import { useEffect, useState } from 'react';

/**
 * Whole seconds until `target`, re-rendering only when that number changes.
 * Deliberately coarse — see §7 on timing tells. It wakes once a second, just
 * after the displayed value rolls over, rather than polling on a short
 * interval: a poll re-renders whatever holds it several times a second for a
 * number that changes once.
 */
export function useSecondsUntil(target: number | null): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (target === null) return;
    let id: ReturnType<typeof setTimeout> | undefined;
    const tick = () => {
      const current = Date.now();
      setNow(current);
      const remaining = target - current;
      if (remaining <= 0) return;
      id = setTimeout(tick, (remaining % 1000 || 1000) + 5);
    };
    tick();
    return () => clearTimeout(id);
  }, [target]);
  return secondsUntil(target, now);
}

export function secondsUntil(target: number | null, now: number): number {
  if (target === null) return 0;
  return Math.max(0, Math.ceil((target - now) / 1000));
}
